(() => {
  const getOrCreateLoadingMask = () => {
    let mask = document.querySelector('[data-kb-compare-loading]');
    if (mask) return mask;
    mask = document.createElement('div');
    mask.setAttribute('data-kb-compare-loading', '');
    mask.hidden = true;
    mask.innerHTML = '<div class="kb-compare__loading-spinner" aria-hidden="true"></div>';
    document.body.appendChild(mask);
    return mask;
  };

  const setLoading = (isLoading) => {
    const mask = getOrCreateLoadingMask();
    if (isLoading) {
      mask.hidden = false;
      mask.classList.add('is-active');
    } else {
      mask.classList.remove('is-active');
      window.setTimeout(() => {
        mask.hidden = true;
      }, 120);
    }
  };

  const fetchAndSwapSection = async ({ nextUrl, sectionId }) => {
    // Prefer Online Store 2.0 "sections" rendering which preserves the configured blocks/settings
    // for the section instance that exists on the page.
    const reqUrl = new URL(nextUrl.toString());
    reqUrl.searchParams.set('sections', sectionId);

    setLoading(true);
    try {
      const res = await fetch(reqUrl.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const json = await res.json();
      const html = json && typeof json === 'object' ? json[sectionId] : '';
      if (!html) throw new Error('Section JSON missing');

      const temp = document.createElement('div');
      temp.innerHTML = html;
      const nextSection = temp.querySelector('[data-kb-compare]');
      const currentSection = document.querySelector('[data-kb-compare]');
      if (!nextSection || !currentSection) throw new Error('Section HTML missing');

      currentSection.replaceWith(nextSection);
      // Update address bar (without sections param)
      const cleanUrl = new URL(nextUrl.toString());
      cleanUrl.searchParams.delete('sections');
      window.history.pushState({}, '', cleanUrl.toString());

      // Re-init the swapped section
      initSection(nextSection);
    } finally {
      setLoading(false);
    }
  };

  const normalizeSizes = (raw, source, allowedValues) => {
    if (raw == null) return [];
    let arr = [];
    if (Array.isArray(raw)) arr = raw;
    else if (typeof raw === 'string' && raw.trim() !== '') arr = [raw];
    if (source === 'tag' && Array.isArray(allowedValues) && allowedValues.length) {
      const allow = new Set(allowedValues);
      return arr.filter((v) => allow.has(v));
    }
    return arr;
  };

  const escapeHtml = (value) => {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  };

  const buildSpecHtml = (compareTable) => {
    if (!compareTable || typeof compareTable !== 'object') return '';
    const entries = Object.entries(compareTable).filter(([k]) => {
      const key = (k == null ? '' : String(k)).trim().toLowerCase().replace(/[_\s]+/g, ' ');
      return key !== 'short name';
    });
    if (!entries.length) return '';
    return entries
      .map(([k, v]) => {
        const valueText = v == null || v === '' ? '-' : String(v);
        // Allow wrapping at "/" (eg. Wired/BT/2.4Ghz) without changing visuals
        const valueTextSoftWrap = valueText.replaceAll('/', '/\u200B');
        return `<div class="kb-compare__spec-row"><div class="kb-compare__spec-key">${escapeHtml(
          k
        )}</div><div class="kb-compare__spec-value">${escapeHtml(valueTextSoftWrap)}</div></div>`;
      })
      .join('');
  };

  const createRowAligner = (section) => {
    let raf = 0;
    let timer = 0;

    const run = () => {
      raf = 0;
      timer = 0;
      const cols = Array.from(section.querySelectorAll('[data-kb-compare-col]'))
        .filter((c) => !c.hidden)
        .filter((c) => (c.querySelector('[data-kb-compare-body]')?.hidden ? false : true));

      // Align the two dropdown rows (size + product) by taking max height per row.
      if (cols.length > 1) {
        const sizeWraps = cols
          .map((c) => c.querySelector('.kb-compare__controls > .kb-compare__select'))
          .filter(Boolean);
        const comboWraps = cols
          .map((c) => c.querySelector('.kb-compare__controls > .kb-compare__combobox'))
          .filter(Boolean);
        const productBlocks = cols
          .map((c) => c.querySelector('.kb-compare__product'))
          .filter(Boolean);
        const productForms = cols
          .map((c) => c.querySelector('.kb-compare__product-form'))
          .filter(Boolean);

        const groups = [sizeWraps, comboWraps, productBlocks, productForms];
        groups.forEach((group) => group.forEach((el) => (el.style.minHeight = '')));
        groups.forEach((group) => {
          if (group.length < 2) return;
          let maxH = 0;
          group.forEach((el) => {
            const h = el.getBoundingClientRect().height;
            if (h > maxH) maxH = h;
          });
          if (maxH <= 0) return;
          group.forEach((el) => (el.style.minHeight = `${Math.ceil(maxH)}px`));
        });
      }

      const specs = cols
        .map((c) => c.querySelector('[data-kb-compare-spec]'))
        .filter(Boolean);

      if (specs.length < 2) return;

      const rowsBySpec = specs.map((spec) => Array.from(spec.querySelectorAll('.kb-compare__spec-row')));
      const maxRows = Math.max(...rowsBySpec.map((rows) => rows.length));
      if (!maxRows) return;

      // Reset any previous height constraints
      rowsBySpec.forEach((rows) =>
        rows.forEach((row) => {
          row.style.height = '';
        })
      );

      // 相同行已被隐藏，不参与高度对齐
      const isHiddenDiffRow = (row) => !row || row.classList.contains('is-diff-hidden');

      // Measure + apply per row index
      for (let i = 0; i < maxRows; i++) {
        let maxH = 0;
        for (const rows of rowsBySpec) {
          const row = rows[i];
          if (isHiddenDiffRow(row)) continue;
          const h = row.getBoundingClientRect().height;
          if (h > maxH) maxH = h;
        }
        if (maxH <= 0) continue;
        for (const rows of rowsBySpec) {
          const row = rows[i];
          if (isHiddenDiffRow(row)) continue;
          row.style.height = `${Math.ceil(maxH)}px`;
        }
      }
    };

    const schedule = () => {
      if (raf || timer) return;
      // Give layout a moment to settle (images/fonts), then measure.
      raf = window.requestAnimationFrame(() => {
        timer = window.setTimeout(run, 30);
      });
    };

    return schedule;
  };
  
  const initColumn = (col, ctx) => {
    if (col.dataset.kbCompareInited === 'true') return;
    col.dataset.kbCompareInited = 'true';
    const sizeSelect = col.querySelector('[data-kb-compare-size]');
    const trigger = col.querySelector('[data-kb-compare-trigger]');
    const triggerText = col.querySelector('[data-kb-compare-trigger-text]');
    const menu = col.querySelector('[data-kb-compare-menu]');
    const search = col.querySelector('[data-kb-compare-search]');
    const list = col.querySelector('[data-kb-compare-list]');
    const spec = col.querySelector('[data-kb-compare-spec]');
    const image = col.querySelector('[data-kb-compare-image]');
    const titleLink = col.querySelector('[data-kb-compare-title]');
    const price = col.querySelector('[data-kb-compare-price]');
    const saleTips = col.querySelector('[data-kb-compare-sale-tips]');
    const atcBtn = col.querySelector('[data-kb-compare-atc]');
    const variantInput = col.querySelector('.product-variant-id');
    const resetBtn = col.querySelector('[data-kb-compare-reset]');

    const urlLinks = col.querySelectorAll('[data-kb-compare-url]');

    const findSizeForHandle = (handle) => {
      if (!handle) return '';
      for (const s of ctx.sizes) {
        const handles = ctx.sizeToHandles.get(s) || [];
        if (handles.includes(handle)) return s;
      }
      return '';
    };

    const setProduct = (handle) => {
      const p = ctx.productsByHandle.get(handle);
      if (!p) return;

      urlLinks.forEach((a) => {
        a.href = p.url || '#';
      });

      if (image) {
        if (p.image) {
          image.src = p.image;
          image.removeAttribute('hidden');
          const onImgReady = () => {
            if (typeof ctx.scheduleRowAlign === 'function') ctx.scheduleRowAlign();
          };
          if (image.complete) onImgReady();
          else {
            image.addEventListener('load', onImgReady, { once: true });
            image.addEventListener('error', onImgReady, { once: true });
          }
        } else {
          image.removeAttribute('src');
          image.setAttribute('hidden', 'hidden');
        }
        image.alt = p.image_alt || p.title || '';
      }

      if (titleLink) titleLink.textContent = p.title || '';
      if (price) price.innerHTML = p.price_html || '';
      if (saleTips) saleTips.innerHTML = p.sale_tips_html || '';

      if (variantInput) {
        if (p.variant_id) {
          variantInput.value = p.variant_id;
          variantInput.disabled = false;
        } else {
          variantInput.value = '';
          variantInput.disabled = true;
        }
      }

      if (atcBtn) {
        if (p.available && p.variant_id) {
          atcBtn.disabled = false;
          atcBtn.removeAttribute('aria-disabled');
        } else {
          atcBtn.disabled = true;
          atcBtn.setAttribute('aria-disabled', 'true');
        }
      }

      if (spec) spec.innerHTML = buildSpecHtml(p.compare_table);
      // 规格表重绘后按当前开关重新判断哪些行相同
      if (typeof ctx.applyDiffFilter === 'function') ctx.applyDiffFilter();
      if (triggerText) triggerText.textContent = p.title || '';
      if (trigger) {
        const baseLabel =
          trigger.getAttribute('data-base-aria-label') ||
          trigger.getAttribute('aria-label') ||
          'Select product';
        if (!trigger.getAttribute('data-base-aria-label')) {
          trigger.setAttribute('data-base-aria-label', baseLabel);
        }
        trigger.setAttribute(
          'aria-label',
          p.title ? `${baseLabel}: ${p.title}` : baseLabel
        );
      }
      col.dataset.selectedHandle = p.handle || '';
      if (typeof ctx.scheduleRowAlign === 'function') ctx.scheduleRowAlign();
    };

    const renderProductList = (handles) => {
      const selected = col.dataset.selectedHandle;

      list.innerHTML = handles
        .map((handle) => {
          const p = ctx.productsByHandle.get(handle);
          if (!p) return '';
          const isSelected = selected === handle;
          return `<button type="button" class="kb-compare__combo-item" role="option" data-kb-compare-item data-handle="${escapeHtml(
            handle
          )}" aria-selected="${isSelected ? 'true' : 'false'}">${escapeHtml(p.title || '')}</button>`;
        })
        .join('');
    };

    const applySearch = () => {
      const q = (search.value || '').trim().toLowerCase();
      // 搜索范围：当有关键词时，面向整个 collection；无关键词时按 Size 下拉过滤。
      const baseHandles = q ? ctx.allHandles : (ctx.sizeToHandles.get(sizeSelect.value) || []);

      if (!q) {
        renderProductList(baseHandles);
        list.scrollTop = 0;
        return;
      }

      // 匹配项排在最前面（不隐藏其它项），并且保持稳定排序。
      const ranked = baseHandles
        .map((handle, idx) => {
          const p = ctx.productsByHandle.get(handle);
          const t = (p?.title || '').toLowerCase();
          let rank = 2;
          if (t.startsWith(q)) rank = 0;
          else if (t.includes(q)) rank = 1;
          return { handle, idx, rank };
        })
        .sort((a, b) => (a.rank - b.rank) || (a.idx - b.idx))
        .map((x) => x.handle);

      renderProductList(ranked);

      // 更新后滚动到顶部，匹配项会更直观地“往前排列”。
      list.scrollTop = 0;
    };

    const openMenu = () => {
      menu.hidden = false;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      search.value = '';
      applySearch();
      search.focus();
    };

    const closeMenu = () => {
      menu.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    };

    const toggleMenu = () => {
      if (menu.hidden) openMenu();
      else closeMenu();
    };

    const setSize = (value, { chooseDefaultProduct = true } = {}) => {
      sizeSelect.value = value;
      if (chooseDefaultProduct) {
        const handles = ctx.sizeToHandles.get(value) || [];
        if (handles.length) setProduct(handles[0]);
        else setProduct(ctx.defaultHandle);
      }
      renderProductList(ctx.sizeToHandles.get(value) || []);
      applySearch();
    };
    col.__kbSetSize = setSize;
    col.__kbSetProduct = setProduct;

    sizeSelect.addEventListener('change', () => {
      setSize(sizeSelect.value, { chooseDefaultProduct: true });
    });

    trigger.addEventListener('click', toggleMenu);

    // 输入停顿 1 秒后再更新下拉：减少“边打字边抖动”的体验问题。
    let searchDebounceTimer = null;
    search.addEventListener('input', () => {
      if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer);
      searchDebounceTimer = window.setTimeout(() => {
        applySearch();
      }, 1000);
    });
    search.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        trigger.focus();
        return;
      }
      if (e.key === 'Enter') {
        const firstVisible = list.querySelector('[data-kb-compare-item]:not([hidden])');
        if (firstVisible) {
          e.preventDefault();
          const handle = firstVisible.dataset.handle;
          const matchedSize = findSizeForHandle(handle);
          if (matchedSize && matchedSize !== sizeSelect.value) {
            setSize(matchedSize, { chooseDefaultProduct: false });
          }
          setProduct(handle);
          closeMenu();
          trigger.focus();
        }
      }
    });

    list.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-kb-compare-item]');
      if (!btn) return;
      const handle = btn.dataset.handle;
      const matchedSize = findSizeForHandle(handle);
      if (matchedSize && matchedSize !== sizeSelect.value) {
        setSize(matchedSize, { chooseDefaultProduct: false });
      }
      setProduct(handle);
      closeMenu();
      trigger.focus();
    });

    resetBtn.addEventListener('click', () => {
      const idx = Number(col.dataset.index || 0);
      ctx.deactivateCol(idx);
    });

    document.addEventListener('click', (e) => {
      if (menu.hidden) return;
      if (col.contains(e.target)) return;
      closeMenu();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (menu.hidden) return;
      closeMenu();
    });

    sizeSelect.innerHTML = ctx.sizes
      .map((v) => `<option value="${escapeHtml(v)}">${escapeHtml(v)}</option>`)
      .join('');

    // 如果该列在初始化前已经写入 selectedHandle（例如从 URL 参数预填），
    // 则优先使用它；否则使用默认第一个匹配商品。
    const initialHandle = (col.dataset.selectedHandle || '').trim();
    const initialProduct = initialHandle ? ctx.productsByHandle.get(initialHandle) : undefined;
    if (initialProduct) {
      // 为该 product 找到它匹配到的 size（如果没有找到则退回第一个 size）。
      const initialSize = findSizeForHandle(initialHandle) || (ctx.sizes[0] || '');

      // 先切到初始 size，但不要自动选择默认商品；接着手动选择 initialHandle。
      setSize(initialSize, { chooseDefaultProduct: false });
      setProduct(initialHandle);
      renderProductList(ctx.sizeToHandles.get(initialSize) || []);
      applySearch();
    } else {
      setSize(ctx.sizes[0] || '', { chooseDefaultProduct: true });
    }
  };

  const initSection = (section) => {
    const dataEl = section.querySelector('[data-kb-compare-data]');
    if (!dataEl) return;
    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    const products = Array.isArray(data.products) ? data.products : [];
    const filter = data.size_filter || {};
    const showSizeFilter = Boolean(data.show_size_filter);
    let sizes = Array.isArray(filter.values) ? filter.values.filter(Boolean) : [];

    const productsByHandle = new Map();
    products.forEach((p) => {
      if (p && p.handle) productsByHandle.set(p.handle, p);
    });

    if (showSizeFilter && !sizes.length) {
      const derived = new Set();
      products.forEach((p) => {
        const s = normalizeSizes(p.size_raw, filter.source, []);
        s.forEach((v) => derived.add(v));
      });
      sizes = Array.from(derived);
    }

    // 非 keyboards：不显示 Size 下拉，并让第二个下拉默认覆盖整个 collection。
    // 这里用一个虚拟 size = "All" 来复用现有逻辑（无关键词时也能显示全量列表）。
    if (!showSizeFilter) sizes = ['All'];

    const sizeToHandles = new Map();
    sizes.forEach((s) => sizeToHandles.set(s, []));
    const allowed = sizes;

    products.forEach((p) => {
      if (!p?.handle) return;

      if (!showSizeFilter) {
        sizeToHandles.get('All').push(p.handle);
        return;
      }

      const s = normalizeSizes(p.size_raw, filter.source, allowed);
      if (!s.length) return;
      s.forEach((sizeValue) => {
        if (!sizeToHandles.has(sizeValue)) return;
        sizeToHandles.get(sizeValue).push(p.handle);
      });
    });

    const firstSize = sizes[0];
    const defaultHandles = (firstSize && sizeToHandles.get(firstSize)) || [];
    const defaultHandle = defaultHandles[0] || products[0]?.handle || '';

    const allHandles = products
      .map((p) => p?.handle)
      .filter(Boolean)
      .filter((h, idx, arr) => arr.indexOf(h) === idx);

    const ctx = { sizes, productsByHandle, sizeToHandles, defaultHandle, allHandles };
    ctx.scheduleRowAlign = createRowAligner(section);

    const normalizeSpecText = (value) =>
      String(value || '')
        .replace(/\u200B/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    // 一行的可比对内容：规格名 + 规格值。忽略换行用的零宽字符和多余空白。
    const specRowSignature = (row) => {
      const keyText = normalizeSpecText(row.querySelector('.kb-compare__spec-key')?.textContent);
      const valueText = normalizeSpecText(row.querySelector('.kb-compare__spec-value')?.textContent);
      return `${keyText}\u0001${valueText}`;
    };

    // 打开「只看差异」后，按行下标比较当前已选产品。
    // 每一行在所有已选产品里都相同，才给这些行加 is-diff-hidden。
    // 只比较大家都有的行；某款行数更多时，多出来的行不参与比较，保持显示。
    // 已选产品不足 2 个时无法判断差异，全部显示。
    const applyDiffFilter = () => {
      const toggle = section.querySelector('[data-kb-compare-diff]');
      const enabled = Boolean(toggle && toggle.checked);
      section.classList.toggle('is-diffs-only', enabled);

      const rowsBySpec = Array.from(section.querySelectorAll('[data-kb-compare-col]'))
        .filter((col) => !col.hidden)
        .filter((col) => {
          const body = col.querySelector('[data-kb-compare-body]');
          return body && !body.hidden;
        })
        .map((col) => col.querySelector('[data-kb-compare-spec]'))
        .filter(Boolean)
        .map((spec) => Array.from(spec.querySelectorAll('.kb-compare__spec-row')));

      rowsBySpec.forEach((rows) => {
        rows.forEach((row) => {
          row.classList.remove('is-diff-hidden', 'is-stripe');
          row.removeAttribute('aria-hidden');
        });
      });

      if (!enabled || rowsBySpec.length < 2) return;

      const minRows = Math.min(...rowsBySpec.map((rows) => rows.length));
      for (let i = 0; i < minRows; i += 1) {
        const signatures = rowsBySpec.map((rows) => specRowSignature(rows[i]));
        const same = signatures.every((signature) => signature === signatures[0]);
        if (!same) continue;
        rowsBySpec.forEach((rows) => {
          rows[i].classList.add('is-diff-hidden');
          rows[i].setAttribute('aria-hidden', 'true');
        });
      }

      // 隐藏后按仍可见的行重排深浅底，避免相邻两行都是深色
      rowsBySpec.forEach((rows) => {
        let visibleIndex = 0;
        rows.forEach((row) => {
          if (row.classList.contains('is-diff-hidden')) return;
          if (visibleIndex % 2 === 0) row.classList.add('is-stripe');
          visibleIndex += 1;
        });
      });
    };

    ctx.applyDiffFilter = applyDiffFilter;

    const diffToggle = section.querySelector('[data-kb-compare-diff]');
    if (diffToggle) {
      diffToggle.addEventListener('change', () => {
        applyDiffFilter();
        if (typeof ctx.scheduleRowAlign === 'function') ctx.scheduleRowAlign();
      });
    }

    const cols = section.querySelectorAll('[data-kb-compare-col]');
    // Weak network: Liquid renders columns with `hidden` to avoid placeholder flash.
    // Once we have parsed data and are ready to init, show them.
    cols.forEach((c) => {
      c.hidden = false;
    });
    const maxCols = cols.length;

    // Compare category UI with feature images (enhances native <select>)
    const catUi = section.querySelector('[data-kb-cat-ui]');
    const catTrigger = section.querySelector('[data-kb-cat-trigger]');
    const catMenu = section.querySelector('[data-kb-cat-menu]');
    const catList = section.querySelector('[data-kb-cat-list]');
    const catTriggerText = section.querySelector('[data-kb-cat-trigger-text]');
    const catTriggerImg = section.querySelector('[data-kb-cat-trigger-img]');

    const setCatTrigger = (key) => {
      const map = data.collections || {};
      const item = map[key] || {};
      if (catTriggerText) catTriggerText.textContent = item.label || key || '';
      if (catTrigger) {
        const baseLabel =
          catTrigger.getAttribute('data-base-aria-label') ||
          catTrigger.getAttribute('aria-label') ||
          'Compare category';
        if (!catTrigger.getAttribute('data-base-aria-label')) {
          catTrigger.setAttribute('data-base-aria-label', baseLabel);
        }
        const labelText = item.label || key || '';
        catTrigger.setAttribute(
          'aria-label',
          labelText ? `${baseLabel}: ${labelText}` : baseLabel
        );
      }
      if (catTriggerImg) {
        if (item.image) {
          catTriggerImg.src = item.image;
          catTriggerImg.hidden = false;
        } else {
          catTriggerImg.removeAttribute('src');
          catTriggerImg.hidden = true;
        }
        catTriggerImg.alt = '';
      }
    };

    const openCatMenu = () => {
      if (!catMenu) return;
      catMenu.hidden = false;
      if (catTrigger) catTrigger.setAttribute('aria-expanded', 'true');
    };
    const closeCatMenu = () => {
      if (!catMenu) return;
      catMenu.hidden = true;
      if (catTrigger) catTrigger.setAttribute('aria-expanded', 'false');
    };
    const toggleCatMenu = () => {
      if (!catMenu) return;
      if (catMenu.hidden) openCatMenu();
      else closeCatMenu();
    };

    // Compare category 下拉：切换 collection（通过 URL 参数驱动 Liquid 重新渲染 products 数据）
    const categorySelect = section.querySelector('[data-kb-compare-category-select]');
    if (categorySelect) {
      // Build enhanced UI list (feature image + label)
      if (catUi && catTrigger && catMenu && catList) {
        const map = data.collections || {};
        const keys = Object.keys(map);
        if (keys.length) {
          catUi.hidden = false;
          const nativeWrap = categorySelect.closest('.kb-compare__select');
          if (nativeWrap) nativeWrap.style.display = 'none';

          catList.innerHTML = keys
            .map((k) => {
              const item = map[k] || {};
              const img = item.image
                ? `<img class="kb-compare__category-icon" src="${escapeHtml(item.image)}" alt="${escapeHtml(
                    item.label || k
                  )}" width="18" height="18" loading="lazy">`
                : `<span class="kb-compare__category-icon kb-compare__category-icon--empty" aria-hidden="true"></span>`;
              const selected = String(categorySelect.value || '') === String(k);
              return `<button type="button" class="kb-compare__category-item" role="option" data-kb-cat-item data-key="${escapeHtml(
                k
              )}" aria-selected="${selected ? 'true' : 'false'}">${img}<span class="kb-compare__category-item-text">${escapeHtml(
                item.label || k
              )}</span></button>`;
            })
            .join('');

          setCatTrigger((categorySelect.value || '').toLowerCase());

          catTrigger.addEventListener('click', toggleCatMenu);
          catList.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-kb-cat-item]');
            if (!btn) return;
            const key = (btn.dataset.key || '').toLowerCase();
            if (!key) return;
            categorySelect.value = key;
            setCatTrigger(key);
            closeCatMenu();
            categorySelect.dispatchEvent(new Event('change', { bubbles: true }));
          });

          document.addEventListener('click', (e) => {
            if (!catMenu || catMenu.hidden) return;
            if (catUi.contains(e.target)) return;
            closeCatMenu();
          });

          document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            if (!catMenu || catMenu.hidden) return;
            closeCatMenu();
          });
        }
      }

      categorySelect.addEventListener('change', async () => {
        const nextCategory = (categorySelect.value || '').trim().toLowerCase();
        const map = data.collections || {};
        const next = map[nextCategory];
        if (!next?.handle) return;

        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('compare_category', nextCategory);
        nextUrl.searchParams.set('filter.collection', next.handle);
        // 切换分类后：清空已选 products，只保留第 1 列，并让页面按新分类默认第一个产品初始化
        nextUrl.searchParams.delete('filter.products');
        nextUrl.searchParams.delete('products');

        const sectionId = section.dataset.sectionId || '';
        if (!sectionId) {
          window.location.href = nextUrl.toString();
          return;
        }

        // Disable trigger while loading to avoid double actions
        if (catTrigger) catTrigger.setAttribute('aria-disabled', 'true');
        if (catTrigger) catTrigger.disabled = true;

        try {
          await fetchAndSwapSection({ nextUrl, sectionId });
        } catch (e) {
          // Fallback: full reload if section rendering is unavailable.
          window.location.href = nextUrl.toString();
        } finally {
          if (catTrigger) catTrigger.removeAttribute('aria-disabled');
          if (catTrigger) catTrigger.disabled = false;
        }
      });
    }

    if (!showSizeFilter) {
      cols.forEach((col) => {
        const sizeSelect = col.querySelector('[data-kb-compare-size]');
        const sizeWrap = sizeSelect ? sizeSelect.closest('.kb-compare__select') : null;
        if (sizeSelect) {
          sizeSelect.setAttribute('aria-hidden', 'true');
          sizeSelect.tabIndex = -1;
        }
        if (sizeWrap) sizeWrap.style.display = 'none';
      });
    }

    // 每一列独立维护自己的 active 状态；不要用 activeCount 这种“左起连续”的逻辑。
    const urlParams = new URLSearchParams(window.location.search);
    const productsParam = urlParams.get('filter.products') || urlParams.get('products');
    const initialHandles = (productsParam ? productsParam.split(',') : [])
      .map((s) => decodeURIComponent(String(s || '').trim()))
      .filter(Boolean)
      // 只保留当前 collection 数据中存在的 product
      .filter((h) => ctx.productsByHandle.has(h));

    const colActive = Array.from(
      { length: maxCols },
      // 页面初始至少保留一列数据（无 URL 预填时默认激活第 1 列）
      (_, idx) => idx < Math.min(maxCols, Math.max(1, initialHandles.length))
    );

    initialHandles.forEach((handle, idx) => {
      if (!cols[idx]) return;
      cols[idx].dataset.selectedHandle = handle;
    });

    const applyColState = (col, isActive) => {
      const addTile = col.querySelector('[data-kb-compare-add-tile]');
      const body = col.querySelector('[data-kb-compare-body]');
      const resetBtn = col.querySelector('[data-kb-compare-reset]');

      if (isActive) {
        if (addTile) addTile.hidden = true;
        if (body) body.hidden = false;
        if (resetBtn) resetBtn.hidden = false;

        const wasInited = col.dataset.kbCompareInited === 'true';
        initColumn(col, ctx);

        // 只有在“这列被清空过”时，才重新选择默认产品，避免点第三个 `+` 重置第二列。
        const needsDefault = !col.dataset.selectedHandle;
        if (wasInited && needsDefault && typeof col.__kbSetSize === 'function') {
          col.__kbSetSize(ctx.sizes[0] || '', { chooseDefaultProduct: true });
        }
      } else {
        // addTile 的显示由 updateLayout() 控制：初始只显示一个 `+`
        if (addTile) addTile.hidden = true;
        if (body) body.hidden = true;
        if (resetBtn) resetBtn.hidden = true;
        col.dataset.selectedHandle = '';
      }
    };

    const updateLayout = () => {
      // 压缩布局：active 列始终靠左连续显示；inactive 只保留一个用于显示 `+`。
      // 这样“删掉任意一列”后不会留下空位。

      const gridEl = cols[0]?.parentElement;
      if (!gridEl) return;

      const activeCols = [];
      const inactiveCols = [];
      cols.forEach((col, idx) => {
        (colActive[idx] ? activeCols : inactiveCols).push(col);
      });

      const addCol = inactiveCols.length ? inactiveCols[0] : null;
      const orderedVisibleCols = addCol ? activeCols.concat([addCol]) : activeCols.slice();

      const orderedSet = new Set(orderedVisibleCols);

      // 先把可见列按顺序移动到 DOM 最前面（CSS grid 会据此自动左对齐）
      orderedVisibleCols.forEach((col) => gridEl.appendChild(col));

      // 处理可见/隐藏 + addTile 显示
      cols.forEach((col, idx) => {
        const visible = orderedSet.has(col);
        col.hidden = !visible;
        if (!visible) return;

        applyColState(col, colActive[idx]);

        const addTile = col.querySelector('[data-kb-compare-add-tile]');
        if (!addTile) return;

        if (colActive[idx]) {
          addTile.hidden = true;
        } else {
          // 仅对 addCol 显示 `+`
          addTile.hidden = col !== addCol;
        }
      });

      if (typeof ctx.applyDiffFilter === 'function') ctx.applyDiffFilter();
      if (typeof ctx.scheduleRowAlign === 'function') ctx.scheduleRowAlign();
    };

    ctx.deactivateCol = (idx) => {
      if (idx < 0 || idx >= maxCols) return;
      colActive[idx] = false;
      updateLayout();
    };

    cols.forEach((col, idx) => {
      const addBtn = col.querySelector('[data-kb-compare-add]');
      if (!addBtn) return;
      addBtn.addEventListener('click', () => {
        if (colActive[idx]) return;
        colActive[idx] = true;
        updateLayout();
      });
    });

    updateLayout();

    // Re-align on resize (debounced via scheduler)
    if (!section.__kbCompareResizeBound) {
      section.__kbCompareResizeBound = true;
      window.addEventListener('resize', () => {
        if (typeof ctx.scheduleRowAlign === 'function') ctx.scheduleRowAlign();
      });
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-kb-compare]').forEach(initSection);
  });
})();

